import { Attachment, Lesson } from "@/app/entities/Training";
import { fetchStringToFile } from "@/app/utils/fetchStringToFile";
import {
  AttachmentFormValues,
  LessonFormValues,
} from "../../hooks/useClssForm";

export const YOUTUBE_URL_PATTERN =
  /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/(watch\?v=|embed\/)?([a-zA-Z0-9_-]{11})/;

export const extractFileExtension = (filePath: string): string => {
  if (!filePath) return "";
  return filePath?.split?.(".").pop() ?? "";
};

interface LessonMedia {
  videoFile?: File;
  thumbnailFile?: File;
  attachmentFiles: AttachmentFormValues[];
}

export const fetchLessonMedia = async (
  videoUrl?: string,
  thumbnailUrl?: string,
  attachmentUrls: Attachment[] = [],
): Promise<LessonMedia> => {
  const isYoutubeVideo = videoUrl ? YOUTUBE_URL_PATTERN.test(videoUrl) : false;

  try {
    const videoFilePromise =
      videoUrl && !isYoutubeVideo
        ? fetchStringToFile(videoUrl, extractFileExtension(videoUrl))
        : Promise.resolve(undefined);

    const thumbnailFilePromise = thumbnailUrl
      ? fetchStringToFile(thumbnailUrl, extractFileExtension(thumbnailUrl))
      : Promise.resolve(undefined);

    const attachmentFilesPromise = attachmentUrls.length
      ? Promise.all(
          attachmentUrls.map(async (attachment) => {
            if (!attachment.url) return null;

            try {
              const file = await fetchStringToFile(
                attachment.url,
                extractFileExtension(attachment.url),
              );
              return { file, name: attachment.name, _id: attachment._id };
            } catch (error) {
              console.error(`Erro ao baixar anexo ${attachment.name}:`, error);
              return null;
            }
          }),
        )
      : Promise.resolve([]);

    const [videoFile, thumbnailFile, attachmentFiles] = await Promise.all([
      videoFilePromise.catch((err) => {
        console.error("Erro ao baixar vídeo:", err);
        return undefined;
      }),
      thumbnailFilePromise.catch((err) => {
        console.error("Erro ao baixar thumbnail:", err);
        return undefined;
      }),
      attachmentFilesPromise,
    ]);

    return {
      videoFile,
      thumbnailFile,
      attachmentFiles: attachmentFiles.filter((file) => file !== null),
    };
  } catch (error) {
    console.error("Erro geral no fetchLessonMedia:", error);
    return {
      videoFile: undefined,
      thumbnailFile: undefined,
      attachmentFiles: [],
    };
  }
};

export const parseLessonToFormValues = async (
  lesson: Lesson,
): Promise<LessonFormValues> => {
  const isYoutubeVideo = YOUTUBE_URL_PATTERN.test(lesson?.video_url ?? "");

  const media = await fetchLessonMedia(
    lesson?.video_url ?? "",
    lesson?.thumbnail_url ?? "",
    lesson?.attachments ?? [],
  );

  return {
    title: lesson?.name ?? "",
    description: lesson?.description ?? "",
    isYoutubeVideo,
    videoUrl: isYoutubeVideo ? lesson?.video_url : "",
    videoFile: media.videoFile,
    thumbnailFile: media.thumbnailFile,
    files: media.attachmentFiles,
  };
};

export const getFileIcon = (extension: string) => {
  const ext = extension.toLowerCase();

  if (["png", "jpg", "jpeg"].includes(ext)) return "image";
  if (["pdf"].includes(ext)) return "file-text";
  if (["doc", "docx"].includes(ext)) return "file-text";
  if (["mp4", "webm", "ogg"].includes(ext)) return "video";
  if (["xlsx"].includes(ext)) return "table";

  return "file";
};
